import chatApi from '@/api/v1/chat';
import humps from 'humps';

export default {
  namespaced: true,

  state: {
    channelsList: [],
    activeChannel: null,

    messages: [],
    totalMessages: 0,
    isChannelLocked: false,

    isSending: false,
    isMessagesLoading: false,
  },

  mutations: {
    SET_CHANNELS(state, channels) {
      state.channelsList = channels;
    },

    SET_MESSAGES(state, messages) {
      state.messages = messages;
    },

    SET_TOTALS(state, total) {
      state.totalMessages = total;
    },

    SET_CHANNEL_STATUS(state, status) {
      state.isChannelLocked = status;
    },

    INCREMENT_TOTALS(state) {
      state.totalMessages += 1;
    },

    INCREMENT_UNREAD_MESSAGES(state, chatId) {
      const index = state.channelsList.findIndex((channel) => channel.id === parseInt(chatId, 10));
      state.channelsList[index].unreadMessages += 1;
    },

    CLEAR_CHANNEL_UNREAD_MESSAGES(state, chatId) {
      const index = state.channelsList.findIndex((channel) => channel.id === parseInt(chatId, 10));
      state.channelsList[index].unreadMessages = 0;
    },

    CLEAR_ALL_CHANNELS_UNREAD_MESSAGES(state) {
      state.channelsList = state.channelsList.map((channel) => ({ ...channel, unreadMessages: 0 }));
    },

    CHANGE_SENDING_STATUS(state, status) {
      state.isSending = status;
    },

    CHANGE_MESSAGES_LOADING_STATUS(state, status) {
      state.isMessagesLoading = status;
    },

    CHANGE_ACTIVE_CHANNEL(state, id) {
      state.activeChannel = id;
    },

    ADD_NEW_MESSAGE(state, message) {
      state.messages.push(humps.camelizeKeys(message));
    },

    ADD_OLDER_MESSAGES(state, messages) {
      state.messages.unshift(...messages);
    },
  },

  actions: {
    fetchChannels({ commit }, projectId) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return new Promise((resolve) => {
        chatApi.index(projectId)
          .then((resp) => {
            commit('SET_CHANNELS', resp.data);
            resolve();
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
      });
    },

    fetchMessages({ commit }, chatId) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return new Promise((resolve) => {
        chatApi.show(chatId)
          .then((resp) => {
            commit('SET_MESSAGES', resp.data.collection);
            commit('SET_TOTALS', resp.data.totalMessages);
            commit('SET_CHANNEL_STATUS', resp.data.locked);
            resolve();
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
        });
      },

      fetchMoreMessages({ commit, state }, chatId) {
        return new Promise((resolve) => {
          chatApi.show(chatId, { offset: state.messages.length })
          .then((resp) => {
            commit('ADD_OLDER_MESSAGES', resp.data.collection);
            resolve();
          });
        });
      },

      createMessage({ commit }, messageData) {
        commit('CHANGE_SENDING_STATUS', true);

        return new Promise((resolve) => {
          chatApi.create(messageData.id, messageData.formData)
          .then((resp) => {
            resolve(resp);
          })
          .finally(() => {
            commit('CHANGE_SENDING_STATUS', false);
          });
        });
      },

      markAllRead({ state, commit }) {
        commit('CHANGE_LOADING_STATUS', true, { root: true });
        const chatIds = state.channelsList.map((channel) => channel.id);

        chatApi.markAllRead(chatIds)
          .then(() => {
            commit('CLEAR_ALL_CHANNELS_UNREAD_MESSAGES');
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
    },

    setActiveChannel({ commit }, chatId) {
      commit('CHANGE_ACTIVE_CHANNEL', chatId);
    },

    incrementUnreadMessages({ commit, state }, chatId) {
      if (chatId !== state.activeChannel) commit('INCREMENT_UNREAD_MESSAGES', chatId);
    },

    clearUnreadMessages({ commit }, chatId) {
      commit('CLEAR_CHANNEL_UNREAD_MESSAGES', chatId);
    },

    addNewMessage({ commit }, message) {
      commit('ADD_NEW_MESSAGE', message);
      commit('INCREMENT_TOTALS');
    },

    sortChatChannelsByNewestMessage({ commit, state }, chatId) {
      const newOrderChannels = state.channelsList;

      newOrderChannels.unshift(newOrderChannels.splice(
        // eslint-disable-next-line eqeqeq
        newOrderChannels.findIndex((channel) => channel.id == chatId), 1,
      )[0]);

      commit('SET_CHANNELS', newOrderChannels);
    },
  },

  getters: {
    unreadMessagesCount: (state) => state.channelsList
      .filter((channel) => !channel.archived)
      .map((channel) => channel.unreadMessages)
      .reduce((a, b) => a + b, 0),

    projectChannelUnreadMessagesCount: (state) => (id) => {
      let projectChannel = {};

      if (state.channelsList.length) {
        projectChannel = state.channelsList
          .find((channel) => channel.projectId === parseInt(id, 10));
      }

      return projectChannel ? projectChannel.unreadMessages : 0;
    },

    getSingleChatDetails: (state) => (chatId) => state.channelsList.find(
      (channel) => channel.id === parseInt(chatId, 10),
    ),

    getReversedMessages: (state) => {
      const reversedMessageList = [...state.messages].reverse();
      return reversedMessageList;
    },
  },
};
